import React, { useMemo, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { DailyMetricsGrowthPctChart } from "../../pages/ProgramAnalysis/DailyMetricsGrowthPctChart";
import { DailyMetricsLineChart } from "../../pages/ProgramAnalysis/DailyMetricsLineChart";
import {
  buildDailyMetricsInteractionDeltaChartPoints,
  buildSingleVideoGrowthPercentChartPoints,
  type DailyMetricPoint,
  type DailyMetricsXAxisMode,
} from "../../pages/ProgramAnalysis/dailyMetricsChart";

export type ContentDailyMetricsGrowthSectionProps = {
  contentId: string;
  dailyMetrics: DailyMetricPoint[];
};

export const ContentDailyMetricsGrowthSection: React.FC<
  ContentDailyMetricsGrowthSectionProps
> = ({ contentId: _contentId, dailyMetrics }) => {
  const [xAxisMode, setXAxisMode] = useState<DailyMetricsXAxisMode>("days");

  const growthPctChartData = useMemo(
    () => buildSingleVideoGrowthPercentChartPoints(dailyMetrics, xAxisMode),
    [dailyMetrics, xAxisMode],
  );

  const interactionDeltaChartData = useMemo(
    () => buildDailyMetricsInteractionDeltaChartPoints(dailyMetrics, xAxisMode),
    [dailyMetrics, xAxisMode],
  );

  if (dailyMetrics.length === 0) {
    return null;
  }

  if (growthPctChartData.length === 0 && interactionDeltaChartData.length === 0) {
    return null;
  }

  const xAxisControl = (
    <FormControl size="small" sx={{ minWidth: 200 }}>
      <InputLabel id="content-growth-xaxis-label">Eje X</InputLabel>
      <Select
        labelId="content-growth-xaxis-label"
        label="Eje X"
        value={xAxisMode}
        onChange={(e) => setXAxisMode(e.target.value as DailyMetricsXAxisMode)}
        MenuProps={{
          slotProps: {
            paper: {
              sx: {
                bgcolor: "background.paper",
                border: 1,
                borderColor: "divider",
              },
            },
          },
        }}
      >
        <MenuItem value="days">Días desde publicación</MenuItem>
        <MenuItem value="calendar">Fecha</MenuItem>
      </Select>
    </FormControl>
  );

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography
          sx={{
            fontSize: "0.9rem",
            fontWeight: 700,
            color: "text.primary",
            letterSpacing: "0.05em",
          }}
        >
          Métricas diarias
        </Typography>
        {xAxisControl}
      </Box>

      {growthPctChartData.length > 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <DailyMetricsGrowthPctChart data={growthPctChartData} height={160} />
        </Paper>
      ) : null}

      {interactionDeltaChartData.length > 0 ? (
        <Box>
          <Typography
            sx={{
              fontSize: "0.9rem",
              fontWeight: 700,
              color: "text.primary",
              letterSpacing: "0.05em",
              mb: 1,
            }}
          >
            Interacciones ganadas
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <DailyMetricsLineChart
              data={interactionDeltaChartData}
              height={160}
              showViews={false}
              showLikes
              showComments
            />
          </Paper>
        </Box>
      ) : null}
    </Stack>
  );
};
